import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  CurrencyDollarIcon,
  PuzzlePieceIcon,
  SparklesIcon,
  UserGroupIcon,
    WalletIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import reconciliationService from '../api/services/reconciliation.service';
import { formatEthiopianDateTime } from '../utils/ethiopianDateTime';

/* =========================================================
   HELPERS
========================================================= */

function fmtEtb(v: number): string {
  const n = Number.isFinite(v) ? v : 0;
  return `ETB ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function nowTime(): string {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/* =========================================================
   PAGE
========================================================= */

const Reconciliation: React.FC = () => {
  const navigate = useNavigate();
  const today = todayIso();

  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [singleDate, setSingleDate] = useState(today);
  const [singleFrom, setSingleFrom] = useState('00:00');
  const [singleTo, setSingleTo] = useState(nowTime());

  const [fromDate, setFromDate] = useState(today);
  const [fromTime, setFromTime] = useState('00:00');
  const [toDate, setToDate] = useState(today);
  const [toTime, setToTime] = useState(nowTime());

  const params = useMemo(() => {
    if (mode === 'single') {
      return {
        fromDate: singleDate,
        fromTime: singleFrom,
        toDate: singleDate,
        toTime: singleTo,
      };
    }
    return { fromDate, fromTime, toDate, toTime };
  }, [
    mode,
    singleDate,
    singleFrom,
    singleTo,
    fromDate,
    fromTime,
    toDate,
    toTime,
  ]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['reconciliation', params],
    queryFn: () => reconciliationService.get(params),
    staleTime: 2 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const downloadCsv = () => {
    if (!data) return;
    const s = data.summary;
    const rows: [string, string | number][] = [
      ['Window', `${data.window.labelFrom} → ${data.window.labelTo}`],
      ['', ''],
      ['Bingo', ''],
      ['Games Played', s.games],
      ['With AI', s.gamesWithAi],
      ['AI Won', s.gamesAiWon],
      ['Human Stakes In', s.humanStakeIn],
      ['Human Payout Out', s.humanPayoutOut],
      ['Bingo Real Revenue', s.bingoRealRevenue],
      ['', ''],
      ['Spin', ''],
      ['Spin Money Paid', s.spinMoneyPaid],
      ['Spin Items Won (manual)', s.spinItemsWon],
      ['', ''],
      ['Referral & Bonus', ''],
      ['Referral Rewards', s.referralRewards],
      ['Signup Bonuses', s.signupBonuses],
      ['', ''],
      ['Real Revenue', s.realRevenue],
      ['Owed To Humans', s.owedToHumans],
    ];

    const csv = rows
      .map(([k, v]) => `"${k}","${String(v).replace(/"/g, '""')}"`)
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliation_${data.window.labelFrom.replace(
      /[/:,\s]/g,
      '_'
    )}_to_${data.window.labelTo.replace(/[/:,\s]/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/financial-management')}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 mt-1"
            title="Back"
          >
            <ArrowLeftIcon className="w-4 h-4 text-gray-900" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Daily Reconciliation
            </h1>
            <p className="text-sm text-gray-900 mt-0.5">
              የቀን ሒሳብ ማረጋገጫ — የገባ ገንዘብ፣ የወጣ ገንዘብ እና የቀረን ገንዘብ።
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 hover:bg-gray-50"
          >
            <ArrowPathIcon
              className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            disabled={!data}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-white text-sm hover:bg-primary-600 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Date + hour filter */}
      <Card padding="sm" className="mb-5">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="w-5 h-5 text-gray-900" />
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setMode('single')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  mode === 'single'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                Single Day
              </button>
              <button
                onClick={() => setMode('range')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  mode === 'range'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                Range
              </button>
            </div>
          </div>

          {mode === 'single' ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={singleDate}
                max={today}
                onChange={(e) => setSingleDate(e.target.value || today)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-900"
              />
              <ClockIcon className="w-4 h-4 text-gray-900" />
              <input
                type="time"
                value={singleFrom}
                onChange={(e) => setSingleFrom(e.target.value || '00:00')}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-900"
              />
              <span className="text-gray-900 font-medium">→</span>
              <input
                type="time"
                value={singleTo}
                onChange={(e) => setSingleTo(e.target.value || nowTime())}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-900"
              />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value || today)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-900"
              />
              <input
                type="time"
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value || '00:00')}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-900"
              />
              <span className="text-gray-900 font-medium">→</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={today}
                onChange={(e) => setToDate(e.target.value || today)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-900"
              />
              <input
                type="time"
                value={toTime}
                onChange={(e) => setToTime(e.target.value || nowTime())}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-900"
              />
            </div>
          )}

          <div className="text-xs text-gray-900 font-medium">
            {data
              ? `Showing ${formatEthiopianDateTime(
                  params.fromDate,
                  params.fromTime
                )} → ${formatEthiopianDateTime(
                  params.toDate,
                  params.toTime
                )}`
              : 'No data yet'}
          </div>
        </div>
      </Card>

      {/* Loading / Error */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      )}

      {error && (
        <Card>
          <p className="text-sm text-red-600 py-4">
            {error instanceof Error
              ? error.message
              : 'Failed to load reconciliation'}
          </p>
        </Card>
      )}

      {data && (
        <>
          {/* HEADLINE */}
          <Card className="mb-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 p-4 rounded-xl bg-gradient-to-br from-primary-50 to-white border border-primary-100">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyDollarIcon className="w-5 h-5 text-primary-700" />
                  <p className="text-xs font-bold uppercase text-gray-900 tracking-wide">
                    Real Revenue · እውነተኛ ገቢ
                  </p>
                </div>
                <p
                  className={`text-3xl font-bold ${
                    data.summary.realRevenue >= 0
                      ? 'text-primary-700'
                      : 'text-red-600'
                  }`}
                >
                  {fmtEtb(data.summary.realRevenue)}
                </p>
                <p className="text-xs text-gray-900 mt-2">
                  Bingo ({fmtEtb(data.summary.bingoRealRevenue)}) − Spin (
                  {fmtEtb(data.summary.spinMoneyPaid)}) − Rewards (
                  {fmtEtb(
                    data.summary.referralRewards + data.summary.signupBonuses
                  )}
                  )
                </p>
              </div>

              <div className="p-4 rounded-xl border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <WalletIcon className="w-5 h-5 text-gray-900" />
                  <p className="text-xs font-bold uppercase text-gray-900 tracking-wide">
                    Owed To Humans · ለሰዎች ያለብን
                  </p>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {fmtEtb(data.summary.owedToHumans)}
                </p>
                <p className="text-xs text-gray-900 mt-2">
                  Current human wallet balance
                </p>
              </div>
            </div>
          </Card>

          {/* BINGO */}
          <Card className="mb-5">
            <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
              <PuzzlePieceIcon className="w-4 h-4 text-gray-900" />
              Bingo Games · የቢንጎ ጨዋታዎች
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Games Played" value={data.summary.games} />
              <Stat label="With AI" value={data.summary.gamesWithAi} />
              <Stat label="AI Won" value={data.summary.gamesAiWon} />
              <Stat
                label="AI Win %"
                value={
                  data.summary.gamesWithAi > 0
                    ? `${(
                        (data.summary.gamesAiWon /
                          data.summary.gamesWithAi) *
                        100
                      ).toFixed(1)}%`
                    : '—'
                }
              />
            </div>

            <div className="border-t border-gray-100 pt-4 max-w-md">
              <Line
                label="Stakes in"
                value={fmtEtb(data.summary.humanStakeIn)}
                tone="green"
              />
              <Line
                label="Payouts out"
                value={`−${fmtEtb(data.summary.humanPayoutOut)}`}
                tone="red"
              />
              <Line
                label="Net bingo revenue"
                value={fmtEtb(data.summary.bingoRealRevenue)}
                bold
              />
            </div>
          </Card>

          {/* SPIN + REFERRAL + BONUS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <Card>
              <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-gray-900" />
                Spin Wins · የስፒን ውጤቶች
              </h2>
              <Line
                label="Total spins"
                value={String(data.summary.spinItemsWon + 0) === '0'
                  ? '—'
                  : String(data.summary.spinItemsWon)}
                muted
              />
              <Line
                label="Money paid"
                value={`−${fmtEtb(data.summary.spinMoneyPaid)}`}
                tone="red"
              />
              <Line
                label="Items won (manual)"
                value={String(data.summary.spinItemsWon)}
                muted
              />
            </Card>

            <Card>
              <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <UserGroupIcon className="w-4 h-4 text-gray-900" />
                Referral & Bonus · ሪፈራል እና ስጦታ
              </h2>
              <Line
                label="Referral rewards"
                value={`−${fmtEtb(data.summary.referralRewards)}`}
                tone="red"
              />
              <Line
                label="Signup bonuses"
                value={`−${fmtEtb(data.summary.signupBonuses)}`}
                tone="red"
              />
              <Line
                label="Total rewards paid"
                value={`−${fmtEtb(
                  data.summary.referralRewards + data.summary.signupBonuses
                )}`}
                bold
              />
            </Card>
          </div>

          {/* SUMMARY TABLE */}
          <Card>
            <h2 className="text-sm font-bold text-gray-900 mb-4">
              Full Summary · የተሟላ ማጠቃለያ
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-900">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold">Item</th>
                    <th className="px-3 py-2 text-right font-bold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <Row
                    label="Human stakes in"
                    value={fmtEtb(data.summary.humanStakeIn)}
                    tone="green"
                  />
                  <Row
                    label="Human payouts out"
                    value={`−${fmtEtb(data.summary.humanPayoutOut)}`}
                    tone="red"
                  />
                  <Row
                    label="Bingo real revenue · የቢንጎ ገቢ"
                    value={fmtEtb(data.summary.bingoRealRevenue)}
                    bold
                  />

                  <Row
                    label="Spin money paid"
                    value={`−${fmtEtb(data.summary.spinMoneyPaid)}`}
                    tone="red"
                  />
                  <Row
                    label="Referral rewards"
                    value={`−${fmtEtb(data.summary.referralRewards)}`}
                    tone="red"
                  />
                  <Row
                    label="Signup bonuses"
                    value={`−${fmtEtb(data.summary.signupBonuses)}`}
                    tone="red"
                  />

                  <Row
                    label="Real Revenue · እውነተኛ ገቢ"
                    value={fmtEtb(data.summary.realRevenue)}
                    bold
                    highlight
                  />
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

/* =========================================================
   SMALL COMPONENTS
========================================================= */

const Stat: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <div className="p-3 rounded-lg border border-gray-100 bg-gray-50/40">
    <p className="text-[10px] uppercase text-gray-900 tracking-wide font-bold">
      {label}
    </p>
    <p className="text-lg font-bold mt-1 text-gray-900">{value}</p>
  </div>
);

const Line: React.FC<{
  label: string;
  value: string;
  tone?: 'green' | 'red';
  bold?: boolean;
  muted?: boolean;
}> = ({ label, value, tone, bold, muted }) => (
  <div className="flex items-center justify-between py-1.5">
    <span
      className={`text-sm text-gray-900 ${
        bold ? 'font-bold' : 'font-medium'
      } ${muted ? 'opacity-70' : ''}`}
    >
      {label}
    </span>
    <span
      className={`text-sm tabular-nums ${
        tone === 'green'
          ? 'text-green-700'
          : tone === 'red'
          ? 'text-red-600'
          : 'text-gray-900'
      } ${bold ? 'font-bold' : 'font-medium'}`}
    >
      {value}
    </span>
  </div>
);

const Row: React.FC<{
  label: string;
  value: string;
  tone?: 'green' | 'red';
  bold?: boolean;
  highlight?: boolean;
}> = ({ label, value, tone, bold, highlight }) => (
  <tr className={highlight ? 'bg-primary-50/60' : ''}>
    <td className={`px-3 py-2 text-gray-900 ${bold ? 'font-bold' : 'font-medium'}`}>
      {label}
    </td>
    <td
      className={`px-3 py-2 text-right tabular-nums ${
        tone === 'green'
          ? 'text-green-700'
          : tone === 'red'
          ? 'text-red-600'
          : 'text-gray-900'
      } ${bold ? 'font-bold' : 'font-medium'}`}
    >
      {value}
    </td>
  </tr>
);

export default Reconciliation;